import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

function EditPermanentGuestModalForList({ onClose, onSave, guestData }) {
    const [names, setNames] = useState(guestData?.name || "");
    const [via, setVia] = useState(guestData?.via || "");
    const [admissionType, setAdmissionType] = useState(() => {
        if (guestData?.admission_type && guestData.admission_type.includes('%')) {
            return "DISCOUNT";
        }
        return guestData?.admission_type || "FREE";
    });
    
    const [customDiscount, setCustomDiscount] = useState(() => {
        if (guestData?.admission_type && guestData.admission_type.includes('%')) {
            return parseInt(guestData.admission_type) || 50;
        }
        return 50;
    });
    
    const [notes, setNotes] = useState(guestData?.notes || "");
    const [plus, setPlus] = useState(guestData?.plus || 0);
    const [category, setCategory] = useState(guestData?.category || "REGULAR"); // Добавляем состояние категории

    const handleSave = useCallback(() => {
        const guestDataToSave = {
            id: guestData?.id,
            name: names,
            plus,
            via,
            admissionType: admissionType === "DISCOUNT" ? `${customDiscount}%` : "FREE",
            notes,
            category, // Добавляем категорию в данные для сохранения
        };
        onSave(guestDataToSave);
    }, [names, plus, via, admissionType, customDiscount, notes, category, onSave, guestData]);

    const handleClose = useCallback(() => {
        document.activeElement.blur();
        onClose();
    }, [onClose]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                document.activeElement.blur();
                if (names.trim() !== "") {
                    handleSave();
                }
            } else if (e.key === "Escape") {
                e.preventDefault();
                document.activeElement.blur();
                handleClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleSave, handleClose, names]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-96 relative">
                <button
                    onClick={handleClose}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                    title="Close"
                >
                    ×
                </button>

                <h3 className="text-lg font-bold mb-4">Edit Permanent Guest</h3>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Names</label>
                    <input
                        type="text"
                        className="w-full border rounded p-2 mt-1"
                        placeholder="Enter guest name, e.g., John Doe"
                        value={names}
                        onChange={(e) => setNames(e.target.value)}
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Via</label>
                    <input
                        className="w-full border rounded p-2 mt-1"
                        placeholder="Enter source, e.g., Admin"
                        value={via}
                        onChange={(e) => setVia(e.target.value)}
                    />
                </div>
                
                {/* Добавляем селектор категории */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <select
                        className="w-full border rounded-md p-1.5 text-sm"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        >
                        <option value="REGULAR">Regular</option>
                        <option value="VIP">VIP</option>
                        <option value="BACKSTAGE">Backstage</option>
                        <option value="ARTIST">Artist</option>
                        <option value="STAFF">Staff</option>
                    </select>
                </div>

                <div className="mb-4">
                    <div className="grid grid-cols-3 items-start gap-x-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Admission</label>
                            <div className="flex items-center space-x-4 mt-1">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="admission"
                                        value="FREE"
                                        checked={admissionType === "FREE"}
                                        onChange={() => setAdmissionType("FREE")}
                                        className="mr-2"
                                    />
                                    Free
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="admission"
                                        value="DISCOUNT"
                                        checked={admissionType === "DISCOUNT"}
                                        onChange={() => setAdmissionType("DISCOUNT")}
                                        className="mr-2"
                                    />
                                    <span className="flex items-center">
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={customDiscount}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const newVal = val === "" ? 0 : Number(val);
                                                const limitedVal = Math.min(100, Math.max(0, newVal));
                                                setCustomDiscount(limitedVal);
                                            }}
                                            className="w-16 border rounded p-1 text-center"
                                            disabled={admissionType !== "DISCOUNT"}
                                        />
                                        %
                                    </span>
                                </label>
                            </div>
                        </div>
                        <div className="border-l -ml-12 pl-12">
                            <label className="block text-sm font-medium text-gray-700">Plus</label>
                            <input
                                type="number"
                                min={0}
                                className="w-16 border rounded p-1 text-center mt-1"
                                placeholder="0"
                                value={plus}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const newVal = val === "" ? 0 : Number(val);
                                    setPlus(newVal);
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                        className="w-full border rounded p-2 mt-1"
                        rows={3}
                        placeholder="Additional notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </div>
                <div className="flex justify-center space-x-2">
                    <button
                        className="px-4 py-2 rounded bg-yellow-500 text-white hover:bg-yellow-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleSave}
                        disabled={!names.trim()}
                    >
                        Save Changes
                    </button>
                    <button
                        className="px-4 py-2 rounded bg-gray-300 text-gray-800 hover:bg-gray-400 focus:outline-none transition"
                        onClick={handleClose}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

EditPermanentGuestModalForList.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    guestData: PropTypes.object,
};

export default EditPermanentGuestModalForList;